import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { BATH_STEPS } from '@/data/site';
import { EASE, useLive } from '@/lib/experience';
import { SectionHeading } from './fx/SectionHeading';
import { burst } from './fx/Burst';
import { Bubbles } from './fx/Bubbles';
import { Sparkle } from './brand/Shapes';
import { Wave } from './fx/Wave';

const BG = '#E8F4FD'; // azul-água clarinho da paleta original

// cartão pastel + emoji de cada etapa
const STEP_STYLE = [
  { card: 'bg-white', emoji: '🫧' },
  { card: 'bg-pet-peach-soft', emoji: '💨' },
  { card: 'bg-[#F1F8E6]', emoji: '🪮' },
  { card: 'bg-pet-pink-soft', emoji: '🌸' },
];

/** Ilustração da etapa: entra como uma bolha de sabão se abrindo sobre a anterior. */
const Art = ({ step, on, reduce }) => {
  const shown = reduce ? { opacity: 1 } : { clipPath: 'circle(75% at 50% 55%)', scale: 1 };
  const hidden = reduce ? { opacity: 0 } : { clipPath: 'circle(0% at 50% 55%)', scale: 1.18 };
  return (
    <motion.img
      src={step.art.lg}
      srcSet={step.art.srcSet}
      sizes="(min-width: 1024px) 34vw, 100vw"
      alt={on ? step.art.alt : ''}
      aria-hidden={!on}
      draggable={false}
      className="absolute inset-0 w-full h-full object-cover"
      style={{ zIndex: on ? 3 : 1, objectPosition: step.art.pos }}
      initial={false}
      animate={on ? shown : hidden}
      // a anterior só some depois que a nova cobriu tudo (sem "flash" de fundo)
      transition={on ? { duration: 1.1, ease: EASE } : { delay: 1.1, duration: 0 }}
    />
  );
};

export const Ritual = () => {
  const reduce = useReducedMotion();
  const [active, setActive] = useState(0);
  const stepRefs = useRef([]);
  const numRefs = useRef([]);
  const stickyRef = useRef(null);
  const sectionRef = useRef(null);
  const live = useLive(sectionRef);
  const firstRun = useRef(true);

  // estrelinhas saindo do número quando a etapa fica ativa
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    const el = numRefs.current[active];
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (r.bottom < 0 || r.top > window.innerHeight) return;
    burst({ clientX: r.left + r.width / 2, clientY: r.top + r.height / 2 }, 'star', 7);
  }, [active]);

  // Etapa ativa = a que está mais perto do centro da área de leitura
  // (desktop: centro da tela; mobile: centro do espaço abaixo da imagem fixa).
  useEffect(() => {
    let frame = null;
    const update = () => {
      frame = null;
      const vh = window.innerHeight;
      const desktop = window.innerWidth >= 1024;
      const artBottom = stickyRef.current?.getBoundingClientRect().bottom ?? 0;
      const target = desktop ? vh / 2 : (Math.max(artBottom, 0) + vh) / 2;
      let best = 0;
      let bestDist = Infinity;
      stepRefs.current.forEach((el, i) => {
        if (!el) return;
        const content = el.firstElementChild || el;
        const r = content.getBoundingClientRect();
        const dist = Math.abs(r.top + r.height / 2 - target);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      });
      setActive(best);
    };
    const onScroll = () => {
      if (frame === null) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, []);

  const step = BATH_STEPS[active];

  return (
    <section ref={sectionRef} data-paused={!live} id="ritual" data-nav="banho" aria-label="O que está incluso no banho" className="relative" style={{ background: BG }}>
      <Wave color={BG} animated className="absolute bottom-[calc(100%-1px)] inset-x-0" />
      <Bubbles count={7} className="opacity-80" />

      <div className="container-cp relative pt-20 md:pt-32 pb-12 md:pb-24">
        <SectionHeading
          align="center"
          kicker="O que está incluso no banho"
          icon={Sparkle}
          kickerClass="bg-white text-pet-blue"
          parts={[{ text: 'Limpinho &' }, { text: 'cheiroso!', className: 'text-pet-pink-deep sticker-text' }]}
          note="Banho com produtos hipoalergênicos, secagem, escovação e perfume. Role e acompanhe cada etapa 👇"
          className="mb-4 lg:mb-16"
        />

        <div className="grid lg:grid-cols-2 gap-x-16">
          {/* ilustração fixa, trocando a cada etapa */}
          <div
            ref={stickyRef}
            className="sticky top-0 pt-[80px] md:pt-[92px] pb-3 lg:top-[12vh] lg:pt-0 lg:pb-0 z-20 lg:self-start lg:!bg-transparent"
            style={{ background: BG }}
          >
            <div
              className="relative mx-auto w-full h-[40svh] sm:h-[48svh] lg:w-auto lg:h-[76vh] lg:aspect-[2/3] rounded-[32px] md:rounded-[44px] overflow-hidden bg-pet-sky shadow-[0_30px_60px_-30px_rgba(77,150,255,0.8)] ring-[7px] ring-white"
              data-cursor={step.title}
            >
              {BATH_STEPS.map((s, i) => (
                <Art key={s.n} step={s} on={i === active} reduce={reduce} />
              ))}

              <Bubbles count={4} travel="60vh" className="z-[4]" />

              <div className="absolute left-3 bottom-3 md:left-5 md:bottom-5 z-[5] hidden lg:flex items-center gap-3 rounded-full bg-white/95 pl-4 pr-5 py-2 shadow-lg">
                <AnimatePresence mode="wait">
                  <motion.span
                    key={step.n}
                    className="flex items-baseline gap-2.5"
                    initial={{ y: 14, opacity: 0, scale: 0.6 }}
                    animate={{ y: 0, opacity: 1, scale: 1 }}
                    exit={{ y: -14, opacity: 0 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 16 }}
                  >
                    <span className="font-display text-sm text-pet-blue">{step.n}</span>
                    <span className="font-display text-2xl md:text-3xl leading-none text-pet-ink">{step.title}</span>
                  </motion.span>
                </AnimatePresence>
              </div>

              <div className="absolute right-4 bottom-6 md:right-6 md:bottom-8 z-[5] flex gap-1.5" aria-hidden="true">
                {BATH_STEPS.map((s, i) => (
                  <span
                    key={s.n}
                    className={`h-2 rounded-full transition-all duration-500 ${i === active ? 'w-7 bg-pet-pink-deep' : 'w-2 bg-white/90'}`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* etapas */}
          <ol className="relative z-10">
            {BATH_STEPS.map((s, i) => {
              const isActive = i === active;
              const st = STEP_STYLE[i];
              return (
                <li
                  key={s.n}
                  ref={(el) => (stepRefs.current[i] = el)}
                  className="min-h-[44svh] lg:min-h-[76vh] flex flex-col justify-center py-5 lg:py-10"
                >
                  <motion.div
                    className={`rounded-[36px] ${st.card} p-6 md:p-8 shadow-[0_20px_50px_-30px_rgba(77,150,255,0.6)] max-w-lg`}
                    animate={
                      isActive
                        ? { opacity: 1, scale: 1, rotate: [0, -3, 3, 0] }
                        : { opacity: 0.45, scale: 0.94, rotate: 0 }
                    }
                    transition={{ type: 'spring', stiffness: 260, damping: 16, rotate: { type: 'tween', duration: 0.6 } }}
                    style={{ originX: 0 }}
                  >
                    <div className="flex items-center gap-4">
                      <span
                        ref={(el) => (numRefs.current[i] = el)}
                        className="bubble w-16 h-16 md:w-20 md:h-20 shrink-0 flex items-center justify-center font-display text-2xl md:text-3xl text-pet-blue"
                      >
                        {s.n}
                      </span>
                      <h3 className="font-display font-medium text-[2.3rem] md:text-[3.2rem] leading-none text-pet-ink">
                        {s.title} <span className="text-[0.8em]">{st.emoji}</span>
                      </h3>
                    </div>
                    <p className="mt-4 text-[17px] md:text-xl leading-relaxed font-semibold text-pet-ink/75">{s.text}</p>
                  </motion.div>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
};

export default Ritual;
